import { isNotDefined } from '@/utils/assertions';

/**
 * Given a string to be searched and a regexp to split it,
 * returns a decomposition of the searchee unsing the given regexp.
 * The decomposition is an odd-length array whose side elements does not match the regexp
 * and internal elements are alternatively matching and unmatching parts.
 *
 * for instance :
 *   separate("I was bored, she WAS sad", /was/gi)
 *     gives
 *   ["I ","was", " bored, she ", "WAS", " sad"]
 *
 * invariant : even index parts never match the regexp, odd index parts always match
 *
 * @param searchee the string to separate into pieces
 * @param searched the regexp to be found in the searchee
 * @returns the alternating array
 */
export function separate(searchee: string, searched: RegExp): string[] {
  if (!searched.flags.includes('g')) {
    // would crash anyway
    throw Error("dev error : must call 'separate' with 'g' regex");
  }

  if (isNotDefined(searchee)) {
    return [''];
  }

  const matches = Array.from(searchee?.matchAll(searched));

  const parts = [];

  let prevMatch = { 0: '', index: 0, length: 0 };

  for (const match of matches) {
    // before this match but after the previous match
    parts.push(searchee.substring(prevMatch.index + prevMatch[0].length, match.index));
    // the match
    parts.push(searchee.substring(match.index, match.index + match[0].length));
    prevMatch = match;
  }
  // after the last match
  parts.push(searchee.substring(prevMatch.index + prevMatch[0].length, searchee.length));

  return parts;
}

/**
 * Same as function separate
 * Except if will merge together unmatching parts separated by empty strings
 * exemple : will not return ['', 'a', '', 'a', ''] but ['','aa', ''] instead
 * this produces lighter dom if the separation is used for formatting text
 * @param searchee the string to separate into pieces
 * @param searched the regexp to be found in the searchee
 * @returns the alternating array
 */
export function separateAndMinimize(searchee: string, searched: RegExp): string[] {
  if (isNotDefined(searchee)) {
    return [''];
  }
  const separation = separate(searchee, searched);

  if (separation.length < 5) {
    // no merging can occur with that few elements
    return separation;
  }

  const firstUnmatching = separation.shift()!;
  const lastUnmatching = separation.pop()!;

  // from now, separation is at least three parts longs, and still odd-length alternative matching/unmatching parts

  const minimized = [];
  let currentMergedPart = separation.shift()!;

  // from now, separation is a an even-length array of alternating part unmatching, then matching, and so on ...
  for (let i = 0; i < separation.length; i += 2) {
    if (separation[i].length === 0) {
      // mergeable !
      currentMergedPart += separation[i + 1];
    } else {
      minimized.push(currentMergedPart, separation[i]);
      currentMergedPart = separation[i + 1];
    }
  }

  minimized.push(currentMergedPart);

  return [firstUnmatching, ...minimized, lastUnmatching];
}
